import { format } from 'date-fns';
import type { InvoiceSource, PhieuThuDetailRow, PhieuThuFormInitialData } from '../types';
import { resolvePaymentDocumentInvoiceId } from '@/lib/payment-document-utils';
import { transformDocumentNumber } from '@/components/custom/erp';
import type { FormFieldState } from '../hooks/useFormFieldState';
import type { ReceiptVoucherFormData } from '../schema';
import { isValidUUID } from '@/lib/uuid-validator';
import { PhieuThuInput } from '@/types/schemas';
import { MA_CHUNG_TU } from '@/constants';
import { FormMode } from '@/types/form';

/**
 * Transform detail rows for PhieuThu API submission
 * @param detailRows - Array of detail row data from the form
 * @param dien_giai
 * @param formMode
 * @returns Transformed detail rows ready for API submission
 */
export const transformDetailRows = (
  detailRows: PhieuThuDetailRow[],
  dien_giai: string | undefined,
  formMode: FormMode,
  maNgv: string | undefined
) => {
  if (!detailRows || detailRows.length === 0) return [];

  return detailRows.map((row, index: number) => ({
    line: index + 1,
    ...(isValidUUID(row.uuid) && formMode !== 'add' && { uuid: row.uuid }),
    dien_giai: row.dien_giai || dien_giai,
    ma_kh: row.ma_kh_data?.uuid || row.ma_kh || '',
    id_hd: resolvePaymentDocumentInvoiceId(maNgv, row.id_hd_data?.ID, row.id_hd),
    tk_co: row.tk_co_data?.uuid || row.id_hd_data?.tk_data?.uuid || '',
    ty_gia_hd: row.ty_gia_hd?.toString() || '0',
    ty_gia2: row.ty_gia2,
    tien_nt: String(row.tien_nt || row.id_hd_data?.tien_con_phai_tt || 0),
    tien: String(row.tien_nt || 0),
    ma_bp: row.ma_bp_data?.uuid || row.ma_bp || '',
    ma_vv: row.ma_vv_data?.uuid || row.ma_vv || '',
    ma_hd: row.ma_hd_data?.uuid || row.ma_hd || '',
    ma_dtt: row.ma_dtt_data?.uuid || row.ma_dtt || '',
    ma_ku: row.ma_ku_data?.uuid || row.ma_ku || '',
    ma_phi: row.ma_phi_data?.uuid || row.ma_phi || '',
    ma_sp: row.ma_sp_data?.uuid || row.ma_sp || '',
    ma_lsx: row.ma_lsx || '',
    ma_cp0: row.ma_cp0_data?.uuid || row.ma_cp0 || ''
  }));
};

/**
 * Transform base form data for PhieuThu submission
 * @param data - Form data from the form submission
 * @param state - Form field state containing references to selected entities
 * @returns Transformed base data ready for API submission
 */
export const transformBaseData = (
  data: ReceiptVoucherFormData,
  state: FormFieldState
): Partial<PhieuThuInput> & Pick<PhieuThuInput, 'tk'> => {
  return {
    ma_ngv: data.ma_ngv || '',
    dia_chi: data.dia_chi || '',
    ong_ba: data.ong_ba || '',
    dien_giai: data.dien_giai || '',

    tk: state.account?.uuid || '',

    ...(() => {
      const docNumber = transformDocumentNumber(state.quyenChungTu, state.soChungTu, MA_CHUNG_TU.TIEN_MAT.PHIEU_THU);
      return {
        ...docNumber,
        i_so_ct: docNumber.i_so_ct?.toString() || ''
      };
    })(),

    ngay_ct: data.ngay_ct ? format(new Date(data.ngay_ct), 'yyyy-MM-dd') : '',
    ngay_lct: data.ngay_ct ? format(new Date(data.ngay_ct), 'yyyy-MM-dd') : '',
    ngay_ct0: data.ngay_ct0 ? format(new Date(data.ngay_ct0), 'yyyy-MM-dd') : '',

    ma_nt: data.ma_nt || '',
    ty_gia: data.ty_gia?.toString() || '1',

    status: data.status || '0',
    so_ct0: data.so_ct0 || '',
    so_ct_goc: data.so_ct_goc || 0,
    dien_giai_ct_goc: data.dien_giai_ct_goc || '',

    ma_tt: state.hanThanhToan?.uuid || ''
  };
};

/**
 * Transform complete form data including detail rows for full submission
 * @param data - Form data from the form submission
 * @param state - Form field state containing references to selected entities
 * @param detailRows - Array of detail row data
 * @param calculatedData - Calculated totals from the form
 * @param entityUnit - Entity unit information
 * @param formMode
 * @returns Complete transformed data ready for API submission
 */
export const transformFormData = (
  data: ReceiptVoucherFormData,
  state: FormFieldState,
  detailRows: PhieuThuDetailRow[] = [],
  calculatedData: { t_tien_nt: number; t_tien: number },
  entityUnit: { uuid?: string } | null | undefined,
  formMode: FormMode
): PhieuThuInput => {
  const baseData = transformBaseData(data, state);
  const child_items = transformDetailRows(detailRows, data.dien_giai || '', formMode, data.ma_ngv);

  return {
    ...baseData,
    unit_id: entityUnit?.uuid || '',

    t_tien_nt: String(calculatedData.t_tien_nt),
    t_tien: String(calculatedData.t_tien),
    ma_kh: child_items[0]?.ma_kh || '',

    child_items
  };
};

export const transformDataFromHoaDon = (hoaDonData?: InvoiceSource): Partial<PhieuThuFormInitialData> => {
  const baseData = {
    ma_ngv: '1',
    dia_chi: hoaDonData?.dia_chi || '',
    ong_ba: hoaDonData?.ong_ba || '',
    dien_giai: `Thu tiền: ${hoaDonData?.dien_giai || ''}`,
    ma_nt: hoaDonData?.ma_nt,
    ty_gia: hoaDonData?.ty_gia?.toString(),
    status: '5',
    transfer_yn: true,
    tk_data: null,
    ngay_ct0: new Date().toISOString().split('T')[0],
    so_ct: undefined
  };
  const chi_tiet = hoaDonData && [
    {
      uuid: `temp-${hoaDonData.uuid || Date.now()}`,
      line: 1,
      dien_giai: `Thu tiền: ${hoaDonData?.dien_giai || ''}`,
      ma_kh_data: hoaDonData?.ma_kh_data,
      id_hd_data: {
        ID: hoaDonData?.uuid || '',
        da_thanh_toan: 0,
        ma_ct: hoaDonData?.ma_ct || '',
        ngay_ct: hoaDonData?.ngay_ct || '',
        so_ct: hoaDonData?.so_ct || '',
        tien_con_phai_tt: hoaDonData?.t_tt || 0,
        tien_hd_nt: hoaDonData?.t_tt_nt || 0,
        tien_tren_hd: hoaDonData?.t_tt_nt || 0,
        con_lai: hoaDonData?.t_tt || 0,
        ngoai_te: hoaDonData?.ma_nt_data?.ma_nt || '',
        tk_data: {
          uuid: hoaDonData?.tk_data?.uuid || '',
          tk: hoaDonData?.tk_data?.code || ''
        }
      },
      tien_nt: hoaDonData.t_tt_nt,
      tien: hoaDonData.t_tt
    } satisfies PhieuThuDetailRow
  ];
  return {
    ...baseData,
    child_data: chi_tiet
  };
};
